import {Component, Inject} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {DialogBaseComponent} from '../dialog-base/dialog-base.component';
import {MatFormField, MatHint, MatInput, MatInputModule, MatLabel} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {UIStateEnum} from 'src/app/enum/UIStateEnum';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {IUpdateUnpauseDate} from "../../../interfaces/IUpdateUnpauseDate";
import {ActiveUserService} from "../../../services/active-user.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {GameService} from "../../../services/game.service";
import {IApiErrorResponse} from "../../../interfaces/error/IApiErrorResponse";

@Component({
  selector: 'app-change-unpause-date-dialog',
  standalone: true,
  imports: [DialogBaseComponent,
    ReactiveFormsModule,
    MatButton,
    MatCheckbox,
    MatInput,
    MatLabel,
    MatHint,
    MatFormField,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule],
  templateUrl: './change-unpause-date-dialog.component.html',
  styleUrl: './change-unpause-date-dialog.component.css'
})
export class ChangeUnpauseDateDialogComponent {
  public uiState = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;

  public unpauseGameDateOption = new FormControl<string | null>(null, [Validators.required]);
  public pauseIndefinitelyOption = new FormControl<boolean>(false, {nonNullable: true});

  public changeUnpauseDateForm = new FormGroup({
    unpauseDate: this.unpauseGameDateOption,
    pauseIndefinitely: this.pauseIndefinitelyOption
  });

  constructor(private activeUserService: ActiveUserService,
              private snackBarService: SnackbarService,
              private gameService: GameService,
              private matDialogRef: MatDialogRef<ChangeUnpauseDateDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: {
                game: IGameQueryResult
              }) {
    this.setDateTimePicker();

    this.pauseIndefinitelyOption.valueChanges.subscribe(indefiniteP => {
      this.unpauseGameDateOption.setValidators(indefiniteP ? [] : [Validators.required]);
      this.unpauseGameDateOption.updateValueAndValidity();
    });

    this.pauseIndefinitelyOption.setValue(!this.data.game?.UnpauseOn);
  }

  private setDateTimePicker() {
    // Use current unpause date if available, otherwise default to 7 days from now
    const currentUnpauseDate = this.data.game?.UnpauseOn
      ? new Date(this.data.game.UnpauseOn)
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    const year = currentUnpauseDate.getFullYear();
    const month = String(currentUnpauseDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentUnpauseDate.getDate()).padStart(2, '0');
    const hours = String(currentUnpauseDate.getHours()).padStart(2, '0');
    const minutes = String(currentUnpauseDate.getMinutes()).padStart(2, '0');
    this.unpauseGameDateOption.setValue(`${year}-${month}-${day}T${hours}:${minutes}`);
  }

  submit() {
    this.uiState = UIStateEnum.ShowLoading;

    if (this.changeUnpauseDateForm.invalid) {
      this.changeUnpauseDateForm.markAllAsTouched();
      this.uiState = UIStateEnum.ShowData;
      return;
    }

    let unpauseDate = this.pauseIndefinitelyOption.value
      ? null
      : new Date(this.unpauseGameDateOption.value!);

    let command: IUpdateUnpauseDate = {
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.data.game.Id,
      UnpauseDate: unpauseDate
    }

    this.gameService.updateUnpauseDate(command).subscribe({
      next: () => {
        this.matDialogRef.close(true);
        this.snackBarService.openSuccessfulSnackBar('Successfully updated unpause date.');
      }, error: (res: IApiErrorResponse) => {
        this.uiState = UIStateEnum.ShowData;
        if (res.Error?.ClientErrorMessages?.length > 0) {
          this.snackBarService.openErrorSnackBar(res.Error.ClientErrorMessages.toString());
        } else {
          this.snackBarService.openErrorSnackBar('There was an error updating the unpause date');
        }
      }
    })
  }
}
